import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { services } from "@/data/portfolio";

export const Services = () => {
  const [openId, setOpenId] = useState(null);
  const [hover, setHover] = useState(null);

  return (
    <section id="services" className="relative py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 02 — Capabilities"}
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-6xl">
            WHAT I
            <br />
            <span className="gradient-text">DO BEST</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
            Hover a row to see the proof — click it to open the details.
          </p>
        </motion.div>

        <div className="mt-14">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className="group border-t border-white/10 last:border-b"
            >
              <button
                data-testid={`service-row-${s.id}`}
                onClick={() => setOpenId(openId === s.id ? null : s.id)}
                onMouseEnter={(e) => setHover({ s, x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => setHover({ s, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHover(null)}
                className="flex w-full items-center gap-5 py-7 text-left sm:gap-8 sm:py-9"
              >
                <span className="font-mono text-xs text-cyan-400">{s.num}</span>
                <span
                  className={`flex-1 font-display text-2xl font-bold tracking-tight transition-all duration-500 sm:text-4xl lg:text-5xl ${
                    openId === s.id
                      ? "gradient-text translate-x-2"
                      : "text-slate-400 group-hover:translate-x-2 group-hover:text-slate-50"
                  }`}
                >
                  {s.title}
                </span>
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-500 ${
                    openId === s.id
                      ? "rotate-45 border-violet-400/60 bg-violet-500/15 text-violet-300"
                      : "border-white/15 text-slate-500 group-hover:border-cyan-400/50 group-hover:text-cyan-300"
                  }`}
                >
                  <Plus size={16} />
                </span>
              </button>

              <AnimatePresence initial={false}>
                {openId === s.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                    data-testid={`service-detail-${s.id}`}
                  >
                    <div className="grid grid-cols-1 gap-5 pb-9 sm:grid-cols-12 sm:pl-14">
                      <p className="text-sm leading-relaxed text-slate-400 sm:col-span-7 sm:text-base">
                        {s.desc}
                      </p>
                      <div className="flex flex-wrap content-start gap-2 sm:col-span-5">
                        {s.tags.map((t) => (
                          <span
                            key={t}
                            className="h-fit rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] text-cyan-300"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {hover && (
          <motion.div
            key={hover.s.id}
            initial={{ opacity: 0, scale: 0.85, rotate: 7 }}
            animate={{ opacity: 1, scale: 1, rotate: 3 }}
            exit={{ opacity: 0, scale: 0.9, rotate: 5 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-none fixed z-[60] hidden w-72 overflow-hidden rounded-xl border border-cyan-400/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)] lg:block"
            style={{
              left: hover.x > window.innerWidth * 0.55 ? hover.x - 330 : hover.x + 30,
              top: Math.max(90, Math.min(hover.y - 140, window.innerHeight - 280)),
            }}
            data-testid="service-hover-preview"
          >
            <img
              src={hover.s.image}
              alt={hover.s.title}
              className="h-44 w-full bg-[#0D0E15] object-cover"
            />
            <p className="bg-[#0D0E15] px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-slate-400">
              {hover.s.caption}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
